import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { toKebab } from '@/lib/analytics-slugs'

type Tier = 'authoritative' | 'research' | 'specialist' | 'general'

const TIER_ORDER: Tier[] = ['authoritative', 'research', 'specialist', 'general']

const TIER_COLOUR: Record<Tier, string> = {
  authoritative: 'var(--chart-primary-strong)',
  research: 'var(--chart-primary)',
  specialist: 'var(--chart-accent)',
  general: 'var(--chart-muted)',
}

const TIER_LABEL: Record<Tier, string> = {
  authoritative: 'Authoritative',
  research: 'Research',
  specialist: 'Specialist',
  general: 'General',
}

function prettySection(section: string): string {
  return section
    .split('_')
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export interface SourceTierMixRow {
  section: string
  authoritative: number
  research: number
  specialist: number
  general: number
}

export interface SourceTierMixProps {
  data: SourceTierMixRow[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

/**
 * Vertical stacked column chart: X = content section, stack = source tier
 * (spec §4.2 ②). Click on any segment → `/stories?section=<slug>&tier=<tier>`
 * where the section is kebab-cased via `toKebab` and the tier is already a
 * lowercase English word that needs no conversion.
 *
 * Clickable surfaces are rendered as an accessible a11y-grid beneath the
 * chart (role="group" of <button>s) so keyboard and test users can navigate
 * without pixel-hit-testing an SVG. The recharts `BarChart` also wires its
 * native `onClick` to the same navigate handler for mouse users.
 */
export function SourceTierMix({
  data,
  isLoading,
  isError,
  onRetry,
}: SourceTierMixProps) {
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Source tier mix</h3>
        <Skeleton data-testid="sp4-tier-mix-skeleton" className="h-64 w-full" />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Source tier mix</h3>
        <ErrorState
          title="Couldn't load source tier data."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  const total = data.reduce(
    (s, d) => s + d.authoritative + d.research + d.specialist + d.general,
    0,
  )
  if (data.length === 0 || total === 0) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Source tier mix</h3>
        <p className="text-sm text-muted-foreground">
          No source tier data in the selected range.
        </p>
      </Card>
    )
  }

  const handleSegmentClick = (section: string, tier: Tier) => {
    navigate(`/stories?section=${toKebab(section)}&tier=${tier}`)
  }

  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold mb-3">Source tier mix</h3>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="section"
              tickFormatter={prettySection}
              tick={{ fontSize: 11 }}
            />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              cursor={{ fill: 'var(--chart-empty)', opacity: 0.3 }}
              labelFormatter={(section) => prettySection(String(section))}
            />
            <Legend
              verticalAlign="top"
              align="right"
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value) => TIER_LABEL[value as Tier] ?? value}
            />
            {TIER_ORDER.map((tier) => (
              <Bar
                key={tier}
                dataKey={tier}
                stackId="tier"
                fill={TIER_COLOUR[tier]}
                name={tier}
                onClick={(entry) => {
                  const section =
                    (entry as { payload?: { section?: string } })?.payload
                      ?.section
                  if (typeof section === 'string') {
                    handleSegmentClick(section, tier)
                  }
                }}
                className="cursor-pointer"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Accessible click surface — one button per (section × tier).
          Keyboard + screen-reader + test users navigate here; sighted
          mouse users hit the SVG bars above. */}
      <div
        role="group"
        aria-label="Source tier mix segments"
        className="mt-3 grid gap-1 text-xs"
        style={{
          gridTemplateColumns: `repeat(${TIER_ORDER.length}, minmax(0, 1fr))`,
        }}
      >
        {data.flatMap((row) =>
          TIER_ORDER.map((tier) => {
            const count = row[tier]
            return (
              <button
                key={`${row.section}-${tier}`}
                type="button"
                data-testid={`sp4-tier-segment-${row.section}-${tier}`}
                onClick={() => handleSegmentClick(row.section, tier)}
                className="rounded border border-border bg-muted/40 px-2 py-1 text-left hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={`${prettySection(row.section)} — ${TIER_LABEL[tier]}: ${count} stories`}
              >
                <span className="flex items-center gap-1">
                  <span
                    aria-hidden
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: TIER_COLOUR[tier] }}
                  />
                  <span className="truncate text-muted-foreground">
                    {prettySection(row.section)}
                  </span>
                </span>
                <span className="mt-0.5 block font-medium text-foreground">
                  {TIER_LABEL[tier]}: {count}
                </span>
              </button>
            )
          }),
        )}
      </div>
    </Card>
  )
}
